import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import ListingCard from "../components/ListingCard";
import StarRating from "../components/StarRating";
import MessageComposer from "../components/MessageComposer";
import ReviewModal from "../components/ReviewModal";
import AvatarUploader from "../components/AvatarUploader";
import DeleteAccountModal from "../components/DeleteAccountModal";
import ProfileAvailability from "../components/ProfileAvailability";
import SettingsModal from "../components/SettingsModal";
import { toast } from "sonner";
import { MapPin, MessageSquare, Trash2, CheckCircle2, Star, Settings } from "lucide-react";
import { format, parseISO } from "date-fns";
import { API } from "../lib/api";

export default function Profile() {
  const { id } = useParams();
  const { t } = useI18n();
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [listings, setListings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ count: 0, average: null });
  const [composeOpen, setComposeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [interaction, setInteraction] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/users/${id}`).then(r => setProfile(r.data)).catch(() => setProfile(null)),
      api.get(`/listings?user_id=${id}`).then(r => setListings(r.data)).catch(() => setListings([])),
      api.get(`/reviews?user_id=${id}`).then(r => setReviews(r.data)).catch(() => setReviews([])),
      api.get(`/reviews/summary/${id}`).then(r => setSummary(r.data)).catch(() => setSummary({ count: 0, average: null })),
    ]);
  }, [id]);

  const loadInteraction = () => {
    if (!user || user.id === id) { setInteraction(null); return; }
    api.get(`/users/${id}/interaction`)
      .then(r => setInteraction(r.data))
      .catch(() => setInteraction(null));
  };

  useEffect(() => { loadInteraction(); }, [id, user?.id]);

  if (!profile) return <div className="pt-32 text-center text-sm text-[#1A1F1C]/60">Loading...</div>;

  const isMe = user?.id === profile.id;
  const initials = profile.name.split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase();

  const markComplete = async () => {
    setCompleting(true);
    try {
      await api.post(`/users/${id}/complete`);
      toast.success(t("completion_confirmed"));
      loadInteraction();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally {
      setCompleting(false);
    }
  };

  const reviewsRefresh = () => {
    api.get(`/reviews?user_id=${id}`).then(r => setReviews(r.data)).catch(() => {});
    api.get(`/reviews/summary/${id}`).then(r => setSummary(r.data)).catch(() => {});
    loadInteraction();
  };

  return (
    <div data-testid="profile-page" className="pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 pb-10 border-b border-[#E6E2D8]">
          {isMe ? (
            <AvatarUploader
              user={user}
              onUpdated={(u) => { setProfile({ ...profile, avatar_id: u.avatar_id }); refresh(); }}
              size={96}
            />
          ) : profile.avatar_id ? (
            <img
              data-testid="profile-avatar"
              src={`${API}/files/${profile.avatar_id}`}
              alt={profile.name}
              className="w-24 h-24 rounded-full object-cover"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-[#143122] text-[#FAFAFA] flex items-center justify-center font-serif-display text-3xl">
              {initials}
            </div>
          )}
          <div className="flex-1">
            <div className="overline text-[#143122]">{t(`role_${profile.role}`)}</div>
            <div className="mt-1 flex items-start justify-between gap-3 flex-wrap">
              <h1 data-testid="profile-name" className="font-serif-display text-4xl md:text-5xl text-[#143122]">
                {profile.name}
              </h1>
              {isMe && (
                <button
                  type="button"
                  data-testid="profile-settings-btn"
                  onClick={() => setSettingsOpen(true)}
                  className="text-sm inline-flex items-center gap-1.5 rounded-md border border-[#143122]/30 text-[#143122] hover:bg-[#F4F1E9] px-3 py-1.5 transition-colors"
                >
                  <Settings size={14} strokeWidth={1.75} /> {t("edit_profile")}
                </button>
              )}
            </div>
            {profile.location && (
              <div className="mt-2 flex items-center gap-1.5 text-sm text-[#1A1F1C]/70">
                <MapPin size={14} strokeWidth={1.5} /> {profile.location}
              </div>
            )}
            {profile.bio && (
              <p className="mt-4 text-sm text-[#1A1F1C]/80 max-w-2xl leading-relaxed">
                {profile.bio}
              </p>
            )}
            {summary.count > 0 && (
              <div data-testid="rating-summary" className="mt-4 flex items-center gap-2">
                <StarRating value={Math.round(summary.average)} readOnly size={16} />
                <span className="text-sm text-[#1A1F1C]/70">
                  {summary.average} ({summary.count})
                </span>
              </div>
            )}
            {user && user.id !== profile.id && profile.email !== "examples@kavioura.local" && (
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <button
                  data-testid="profile-message-btn"
                  onClick={() => setComposeOpen(true)}
                  className="kav-btn-primary text-sm inline-flex items-center gap-2"
                >
                  <MessageSquare size={14} strokeWidth={1.75} /> {t("message_user")}
                </button>
                {interaction?.can_complete && (
                  <button
                    data-testid="profile-mark-complete-btn"
                    onClick={markComplete}
                    disabled={completing}
                    className="text-sm inline-flex items-center gap-2 rounded-md border border-[#143122] text-[#143122] hover:bg-[#143122] hover:text-white transition-colors px-4 py-2 disabled:opacity-50"
                  >
                    <CheckCircle2 size={14} strokeWidth={1.75} />
                    {completing ? "..." : t("mark_deal_done")}
                  </button>
                )}
                {interaction?.has_completed && !interaction?.has_reviewed && (
                  <button
                    data-testid="profile-write-review-btn"
                    onClick={() => setReviewOpen(true)}
                    className="text-sm inline-flex items-center gap-2 rounded-md bg-[#B58A2F] text-white hover:bg-[#8f6d20] transition-colors px-4 py-2"
                  >
                    <Star size={14} strokeWidth={1.75} />
                    {t("write_review")}
                  </button>
                )}
                {interaction?.has_reviewed && (
                  <span
                    data-testid="profile-reviewed-badge"
                    className="text-xs inline-flex items-center gap-1.5 text-[#143122]/70 px-3 py-2"
                  >
                    <CheckCircle2 size={12} strokeWidth={1.75} /> {t("you_reviewed")}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Reviews */}
        <div className="mt-12" data-testid="profile-reviews-section">
          <div className="overline text-[#143122]">{t("reviews")}</div>
          <h2 className="mt-2 font-serif-display text-3xl text-[#1A1F1C]">
            {reviews.length} {reviews.length === 1 ? t("review_singular") : t("reviews_plural")}
          </h2>
          <div className="mt-6 grid gap-3 md:grid-cols-2">
            {reviews.length === 0 ? (
              <div className="text-sm text-[#1A1F1C]/60 border border-dashed border-[#E6E2D8] rounded-lg py-10 text-center md:col-span-2">
                {t("no_reviews_yet")}
              </div>
            ) : reviews.map(r => (
              <div key={r.id} data-testid={`review-${r.id}`} className="border border-[#E6E2D8] rounded-lg p-4 bg-white">
                <div className="flex items-center justify-between">
                  <div className="font-serif-display text-lg text-[#143122]">{r.author_name}</div>
                  <StarRating value={r.rating} readOnly size={14} />
                </div>
                {r.comment && <div className="mt-2 text-sm text-[#1A1F1C]/80">{r.comment}</div>}
                <div className="text-xs text-[#1A1F1C]/50 mt-2">
                  {r.context_type} · {format(parseISO(r.created_at), "d MMM yyyy")}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Listings */}
        <div className="mt-12">
          <div className="overline text-[#143122]">{t("listings_by")} {profile.name.split(" ")[0]}</div>
          <h2 className="mt-2 font-serif-display text-3xl text-[#1A1F1C]">
            {listings.length} {listings.length === 1 ? t("listing") : t("listings")}
          </h2>

          <div className="mt-6">
            {listings.length === 0 ? (
              <div className="text-sm text-[#1A1F1C]/60 border border-dashed border-[#E6E2D8] rounded-lg py-16 text-center">
                {t("no_public_listings")}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
              </div>
            )}
          </div>
        </div>

        {/* Availability calendar — providers, stables, workers */}
        <ProfileAvailability profile={profile} />

        {/* Danger zone — own profile only */}
        {isMe && (
          <div data-testid="danger-zone" className="mt-16 pt-8 border-t border-[#E6E2D8]">
            <div className="overline text-[#7A2A2A]">{t("danger_zone")}</div>
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-[#FBF3F1] border border-[#E5C7C2] rounded-lg p-5">
              <div>
                <div className="font-serif-display text-lg text-[#7A2A2A]">
                  {t("delete_account")}
                </div>
                <p className="mt-1 text-sm text-[#1A1F1C]/75 max-w-lg">
                  {t("delete_account_desc")}
                </p>
              </div>
              <button
                type="button"
                data-testid="delete-account-btn"
                onClick={() => setDeleteOpen(true)}
                className="inline-flex items-center gap-2 rounded-md border border-[#7A2A2A] text-[#7A2A2A] hover:bg-[#7A2A2A] hover:text-white transition-colors px-4 py-2 text-sm font-medium"
              >
                <Trash2 size={14} strokeWidth={1.75} />
                {t("delete_account")}
              </button>
            </div>
          </div>
        )}
      </div>
      <MessageComposer open={composeOpen} onClose={() => setComposeOpen(false)} recipient={profile} />
      {isMe && (
        <SettingsModal
          open={settingsOpen}
          onClose={() => {
            setSettingsOpen(false);
            // refresh profile view so new name/bio/location shows
            api.get(`/users/${id}`).then(r => setProfile(r.data)).catch(() => {});
          }}
        />
      )}
      {interaction?.completion_id && (
        <ReviewModal
          open={reviewOpen}
          onClose={() => setReviewOpen(false)}
          context={{ context_type: "completion", context_id: interaction.completion_id }}
          onDone={reviewsRefresh}
        />
      )}
      {isMe && (
        <DeleteAccountModal
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onDeleted={() => { logout(); nav("/"); }}
        />
      )}
    </div>
  );
}
